/**
 * Single DOM button anchored above (or below) the opening. Click rotates
 * the opening so its `u` axis is parallel to the mean of its two
 * neighboring walls' directions.
 *
 * Hidden when either neighbor is missing (no meaningful "average") or
 * when the two neighbors cancel out (already-straight wall — see
 * `computeNeighborDirections` for the avgDir=null semantic).
 *
 * Same DOM/reprojection pattern as `AlignParallelButtons`.
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';

const BUTTON_SIZE_PX = 28;

/**
 * "Average" icon — a horizontal stroke with a small dot above and below,
 * suggesting "split the difference". Inlined SVG so we don't depend on
 * an asset import. Uses currentColor so the button's CSS color drives it.
 */
const AVERAGE_ICON_SVG = `<svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
  <line x1="4" y1="12" x2="20" y2="12"/>
  <circle cx="8" cy="6" r="1.2" fill="currentColor"/>
  <circle cx="16" cy="18" r="1.2" fill="currentColor"/>
</svg>`;

export interface AlignAverageButtonDeps {
  readonly viewer: Viewer;
  /** Called when the user clicks the button. */
  readonly onClick: () => void;
}

export class AlignAverageButton {
  private readonly button: HTMLButtonElement;
  private anchor: THREE.Vector3 | null = null;
  private enabled = false;
  private visible = true;
  private readonly boundReproject: () => void;

  constructor(private readonly deps: AlignAverageButtonDeps) {
    this.button = this.createButton(deps.viewer.translationService.t('Align to average of neighbor walls'));
    this.button.addEventListener('click', (e) => {
      e.stopPropagation();
      this.deps.onClick();
    });
    this.button.addEventListener('mousedown', (e) => e.stopPropagation());
    this.deps.viewer.renderArea.appendChild(this.button);
    this.boundReproject = () => this.reproject();
    this.deps.viewer.addEventListener('update', this.boundReproject);
  }

  /** Set the world anchor. Pass `null` to hide. */
  setAnchor(anchor: THREE.Vector3 | null): void {
    this.anchor = anchor ? anchor.clone() : null;
    this.enabled = anchor !== null;
    this.reproject();
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.reproject();
  }

  dispose(): void {
    this.deps.viewer.removeEventListener('update', this.boundReproject);
    this.button.remove();
  }

  private createButton(ariaLabel: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.setAttribute('aria-label', ariaLabel);
    btn.title = ariaLabel;
    btn.innerHTML = AVERAGE_ICON_SVG;
    btn.style.cssText = `
      position: absolute;
      display: none;
      width: ${BUTTON_SIZE_PX}px;
      height: ${BUTTON_SIZE_PX}px;
      padding: 0;
      align-items: center;
      justify-content: center;
      background: rgba(20, 20, 20, 0.92);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      color: #00ff88;
      cursor: pointer;
      z-index: 10000;
      pointer-events: auto;
    `;
    return btn;
  }

  private reproject(): void {
    if (!this.visible || !this.enabled || !this.anchor) {
      this.button.style.display = 'none';
      return;
    }
    const camera = this.deps.viewer.scene.getActiveCamera();
    const size = this.deps.viewer.renderer.getSize(new THREE.Vector2());
    const v = this.anchor.clone().project(camera);
    if (v.z < -1 || v.z > 1) {
      this.button.style.display = 'none';
      return;
    }
    const canvasX = (v.x + 1) * 0.5 * size.width;
    const canvasY = (-v.y + 1) * 0.5 * size.height;
    this.button.style.display = 'flex';
    this.button.style.left = `${canvasX - BUTTON_SIZE_PX / 2}px`;
    this.button.style.top = `${canvasY - BUTTON_SIZE_PX / 2}px`;
  }
}
